// Theme manager to handle dynamic theme updates
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { systemTheme, ThemeConfig } from '../config/theme';
import useStore from '../store/useStore';

interface ThemeStore {
  theme: ThemeConfig;
  setTheme: (theme: Partial<ThemeConfig>) => void;
  updateTheme: (theme: Partial<ThemeConfig>) => void;
}

// Create a store to manage the theme
export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: systemTheme,
      setTheme: (newTheme: Partial<ThemeConfig>) => set((state) => ({
        theme: { ...state.theme, ...newTheme }
      })),
      updateTheme: (newTheme: Partial<ThemeConfig>) => set((state) => ({
        theme: { ...state.theme, ...newTheme }
      })),
    }),
    {
      name: 'playbpo-theme-storage',
    }
  )
);

// Function to get the current theme
export const getCurrentTheme = (): ThemeConfig => {
  return useThemeStore.getState().theme;
};

// Zera tema e logo de volta ao padrao do sistema. Usado no login pra nao
// herdar tema/logo persistidos de um cliente anterior antes de repreencher.
export const resetTheme = (): void => {
  useThemeStore.getState().setTheme(systemTheme);
  useStore.getState().setLogoBase64(null);
};

// Function to update the theme
export const updateTheme = (template: any): void => {
  const themeUpdate: Partial<ThemeConfig> = {};

  if (template.color1)
    themeUpdate.primaryColor = template.color1;

  if (template.color2)
    themeUpdate.secondaryColor = template.color2;

  themeUpdate.logo = { main: template?.file?.url ?? template?.logo?.main ?? '/logo-financeiro-na-mao.png' };

  useThemeStore.getState().updateTheme(themeUpdate);
};

// Busca o logo pela URL, converte para base64 e salva no store (memória).
export const loadLogoBase64 = async (): Promise<void> => {
  const url = getCurrentTheme().logo.main;
  if (!url) return;
  try {
    const response = await fetch(url);
    if (!response.ok) return;
    const blob = await response.blob();
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    useStore.getState().setLogoBase64(base64);
  } catch {
    console.error('Erro ao buscar logo');
  }
};

export const getLogoBase64 = (): string => {
  return useStore.getState().logoBase64 || getCurrentTheme().logo.main;
};